/**
 * ReportModal - Modal for reporting inappropriate prompts
 * Allows users to select a reason and provide details for reporting content
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import LoginModal from '../auth/LoginModal';
import { promptService } from '../../services/promptService';
import { notificationService } from '../../services/notificationService';
import { useReportReasons } from '../../hooks/queries/useReportReasons';
import { DEFAULT_REPORT_REASONS, getOrderedReasonEntries } from '../../constants/reportReasons';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  promptId: string;
}

export default function ReportModal({ isOpen, onClose, promptId }: ReportModalProps) {
  const { state: { isAuthenticated } } = useAuth();
  const { data: reasonsData, isLoading: isLoadingReasons } = useReportReasons();
  const reportReasonEntries = useMemo(() => {
    const backendReasons = reasonsData?.reasons;

    if (backendReasons && Object.keys(backendReasons).length > 0) {
      return getOrderedReasonEntries(backendReasons);
    }

    return getOrderedReasonEntries(DEFAULT_REPORT_REASONS);
  }, [reasonsData?.reasons]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setDetails('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check authentication
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    if (!selectedReason) {
      setError('Please select a reason for reporting');
      return;
    }

    if (selectedReason === 'other' && !details.trim()) {
      setError('Please provide details for "Other" reason');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await promptService.reportPrompt(promptId, selectedReason, details.trim() || undefined);
      notificationService.success('Report submitted successfully. Thank you for helping keep our community safe.');
      onClose();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to submit report';
      setError(errorMessage);
      notificationService.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Report Prompt"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Reason Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for reporting <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1 sm:pr-2">
            {isLoadingReasons ? (
              <div className="text-sm text-gray-500">Loading reasons…</div>
            ) : (
              reportReasonEntries.map(([key, label]) => (
              <label
                key={key}
                className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="radio"
                  name="reason"
                  value={key}
                  checked={selectedReason === key}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
              ))
            )}
          </div>
        </div>

        {/* Details Textarea */}
        <div>
          <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-2">
            Additional details {selectedReason === 'other' && <span className="text-red-500">*</span>}
          </label>
          <textarea
            id="details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            placeholder="Please provide any additional information that would help us review this report..."
          />
          <p className="mt-1 text-xs text-gray-500">
            {selectedReason === 'other' 
              ? 'Please describe the issue in detail'
              : 'Optional: Provide more context about why you\'re reporting this content'
            }
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || !selectedReason}
            isLoading={isSubmitting}
          >
            Submit Report
          </Button>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
          Reports are reviewed by our moderation team. False reports may result in account restrictions.
        </p>
      </form>
    </Modal>
    
    {/* Login Modal */}
    <LoginModal
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
    />
    </>
  );
}
